import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, NISCO, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import NISProductPriceSavingsMessage from "@tCentro/Components/NISProductPriceSavingsMessage";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: ProductPriceStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                isLoading,
                isSearchPage,
                view,
                productFilters: { pageCategoryId },
                parameter,
            },
        },
    } = state;
    return {
        currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        session: state.context.session,
        showPricing: state.context.showPricing,
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        view,
        pageType: getCurrentPage(state).type,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    priceWrapperGrid?: InjectableCss;
    price?: PriceStyles;
    priceGrid?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    parent?: InjectableCss;
    parentLabel?: InjectableCss;
    unitListPrice?: TypographyProps;
    unitListPriceText?: InjectableCss;
    salePriceLabeltext?: InjectableCss;
    loginContainer?: InjectableCss;
    linkStylesLogin?: InjectableCss;
    loginMessageStyles?: InjectableCss;
    savingPriceWrapper?: InjectableCss;
    savingPriceWrapperGrid?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
    parentLabel?: InjectableCss;
    unitListPrice?: TypographyProps;
    unitListPriceText?: InjectableCss;
    salePriceLabeltext?: InjectableCss;
    savingPriceWrapper?: InjectableCss;
    savingPriceWrapperGrid?: InjectableCss;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const NISProductPriceStyles: ProductPriceStyles = {
    loginContainer: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    linkStylesLogin: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
        `,
    },
    loginMessageStyles: {
        css: css`
            color: ${getColor("text.main")};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-left: 4px;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    priceWrapperGrid: {
        css: css`
            ${wrapInContainerStyles}
            div {
                span[data-test-selector^="productPrice_unitOfMeasureLabel"] {
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles}
            div {
                span[data-test-selector^="productPrice_unitOfMeasureLabel"] {
                    color: ${getColor("text.main")};
                }
            }
            text-align: right;
        `,
    },
    price: {
        errorText: { weight: "bold" },
        parentLabel: {
            css: css`
                display: flex;
                justify-content: right;
                align-items: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css``,
                        css``,
                        css`
                            justify-content: left;
                        `,
                        null,
                    ])}
                & > span {
                    font-size: 20px;
                    margin-right: 5px;
                }

                h4 {
                    font-size: 20px;
                }
            `,
        },

        salePriceLabeltext: {
            css: css`
                font-size: 24px;
                font-weight: 600;
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 18px !important;
                        `,
                        css`
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ])}
                @media (min-width: 992px) and (max-width: 1024px) {
                    font-size: 24px !important;
                }
            `,
        },
        unitListPriceText: {
            css: css`
                margin: 0 !important;
                font-size: 32px;
                font-weight: 600;
                color: #000;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.secondaryFamiliy};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 18px !important;
                        `,
                        css`
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ])}
                @media (min-width: 992px) and (max-width: 1024px) {
                    font-size: 24px !important;
                }
            `,
        },
        unitListPrice: {
            css: css`
                color: #838b90;
                text-decoration: line-through;
                font-size: 18px;
                font-weight: 600;
                margin-left: 0px;
                line-height: 24px;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.secondaryFamiliy};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px !important;
                        `,
                        null,

                        css`
                            font-size: 14px !important;
                        `,
                        null,
                    ])}
            `,
        },

        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    color: ${getProp("theme.typography.p.color")};
                    font-weight: 600;
                    white-space: nowrap;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 18px !important;
                            `,
                            css`
                                font-size: 18px !important;
                            `,
                            null,
                            null,
                        ])}
                    @media (min-width: 992px) and (max-width: 1024px) {
                        font-size: ${TYPOGRAPHY.size.large};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    priceGrid: {
        errorText: { weight: "bold" },
        parentLabel: {
            css: css`
                display: flex;
                justify-content: left;
                align-items: center;
                & > span {
                    font-size: 20px;
                    margin-right: 5px;
                }

                h4 {
                    font-size: 20px;
                }
            `,
        },

        salePriceLabeltext: {
            css: css`
                font-size: 24px;
                font-weight: 600;
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 18px !important;
                        `,
                        css`
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ])}
                @media (min-width: 992px) and (max-width: 1024px) {
                    font-size: 24px !important;
                }
            `,
        },
        unitListPriceText: {
            css: css`
                margin: 0 !important;
                font-size: 32px;
                font-weight: 600;
                color: #000;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.secondaryFamiliy};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 18px !important;
                        `,
                        css`
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ])}
                @media (min-width: 992px) and (max-width: 1024px) {
                    font-size: 24px !important;
                }
            `,
        },
        unitListPrice: {
            css: css`
                color: #838b90;
                text-decoration: line-through;
                font-size: 18px;
                font-weight: 600;
                margin-left: 0px;
                line-height: 24px;
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.secondaryFamiliy};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: 14px !important;
                        `,
                        null,

                        css`
                            font-size: 14px !important;
                        `,
                        null,
                    ])}
            `,
        },

        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    color: ${getProp("theme.typography.p.color")};
                    font-weight: 600;
                    white-space: nowrap;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 18px !important;
                            `,
                            css`
                                font-size: 18px !important;
                            `,
                            null,
                            null,
                        ])}
                    @media (min-width: 992px) and (max-width: 1024px) {
                        font-size: ${TYPOGRAPHY.size.large};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    font-size: 12px !important;
                `,
            },
        },
        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        size: "18px",
        css: css`
            margin-bottom: 5px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: #36603a !important;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.secondaryFamiliy};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: ${TYPOGRAPHY.lineHeight.base};
        `,
    },
    quoteMessage: {
        text: {
            css: css`
                font-family: "Roboto Condensed" !important,
                font-weight: 600 !important;
                font-size: 16px !important;
                line-height: 32px !important; 
                color: #333333 !important;
            `,
        },
    },
    quoteMessageWrapper: {
        css: css``,
    },
    savingPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            text-align: right;
        `,
    },
    savingPriceWrapperGrid: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const SectionWrapper = getStyledWrapper("section");

const NISProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    extendedStyles,
    session,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    view,
    pageType,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(NISProductPriceStyles, extendedStyles));

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;
    const isBrandDetailsPage = pageType === "BrandDetailsPage";

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = view === "Grid" || isBrandDetailsPage ? styles.priceGrid : styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles?.signInText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("Pricing_SignInForPrice")}
                </Typography>
            </StyledWrapper>
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles?.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const isLoggedIn = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
            <StyledWrapper
                {...(view === "Grid" || isBrandDetailsPage ? styles.priceWrapperGrid : styles.priceWrapper)}
                data-test-selector="priceIncludeVat"
            >
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles || {}}
                    showSavings={showSavings}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    savingsMessageText={styles.savingsMessageText}
                    salePriceLabel={
                        productContextModel.product?.salePriceLabel ||
                        cartLineModel?.salePriceLabel ||
                        translate("Sale Price:")
                    }
                    view={view}
                />

                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText}>
                        {translate("Invalid Price")}
                    </Typography>
                )}
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                        showSavings={showSavings}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        savingsMessageText={styles.savingsMessageText}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles?.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {/* {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )} */}
            {/* Removed login for all user */}
            {/* {!isLoggedIn && (
                <StyledWrapper {...styles.loginContainer}>
                    <Link href={`${siteMessage("LogInForPrice_Redirect_link")}`} {...styles.linkStylesLogin}>
                        {siteMessage("LogInForPrice_linkText")}
                    </Link>
                    <Typography as="span" {...styles.loginMessageStyles}>
                        {siteMessage("LogInForPrice_TextMessage")}
                    </Typography>
                </StyledWrapper>
            )} */}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    savingsMessageText?: TypographyPresentationProps;
    view?: string | undefined;
    isBrandDetailsPage?: boolean;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    savingsMessageText,
    view,
    isBrandDetailsPage,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    let greaterThanTwoPercent;
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);

        const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
        const savingsPercent = Math.round((savingsAmount / unitListPrice) * 100);
        greaterThanTwoPercent = savingsPercent > 2;

        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }

    return (
        <>
            {pricing ? (
                <>
                    <StyledWrapper
                        {...(view === "Grid" || isBrandDetailsPage
                            ? styles.savingPriceWrapperGrid
                            : styles.savingPriceWrapper)}
                    >
                        {pricing && showSavings && (
                            <NISProductPriceSavingsMessage
                                pricing={pricing}
                                qtyOrdered={qtyOrdered || 1}
                                showSavingsAmount={showSavingsAmount}
                                showSavingsPercent={showSavingsPercent}
                                currencySymbol={currencySymbol}
                                {...savingsMessageText}
                            />
                        )}

                        {salePriceLabelPrefix && greaterThanTwoPercent ? (
                            <Typography {...styles.unitListPrice}>{pricing.unitListPriceDisplay}</Typography>
                        ) : null}
                    </StyledWrapper>
                    <StyledWrapper {...styles.parentLabel}>
                        {/* Removed Sales Price */}
                        {/* <Typography data-test-selector="saleprice-label" {...styles.salePriceLabeltext}>
                        {salePriceLabelPrefix}
                    </Typography> */}
                        <Typography
                            variant="h4"
                            data-test-selector="productPrice_unitNetPrice"
                            {...styles.unitListPriceText}
                        >
                            {enableVat && displayWithVat
                                ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                                : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                        </Typography>
                        <UnitOfMeasure
                            unitOfMeasure={unitOfMeasure}
                            showUnitOfMeasure={showUnitOfMeasure}
                            styles={styles.unitOfMeasure}
                        />
                    </StyledWrapper>
                </>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice_CurrencySymbol">
                    {currencySymbol}
                </Typography>
            )}

            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(NISProductPrice);
